import { motion } from "framer-motion";
import { Flame, MessageSquareHeart, TrendingUp, Rocket, ShieldCheck, Sparkles, Target, Compass } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Reveal";

const ICONS = [Flame, MessageSquareHeart, TrendingUp, Rocket, ShieldCheck, Sparkles];

export default function CompanyProfile() {
  const { t } = useLanguage();
  const p = t.profile;

  return (
    <section id="profile" data-testid="company-profile-section" className="relative py-20 sm:py-28 bg-white grain overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 end-[-10%] w-[45vw] h-[45vw] rounded-full bg-[#DAE13B]/20 blur-3xl" />
        <div className="absolute -bottom-32 start-[-10%] w-[40vw] h-[40vw] rounded-full bg-[#ABCF39]/15 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-5">
            <div data-testid="profile-mission-card" className="relative h-full rounded-[2rem] p-8 sm:p-10 bg-gradient-to-br from-[#ABCF39] to-[#DAE13B] text-[#383838] overflow-hidden">
              <span className="absolute -bottom-10 -end-10 w-44 h-44 rounded-full bg-white/20" />
              <div className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-2xl bg-[#383838] text-[#DAE13B] flex items-center justify-center shrink-0">
                  <Target size={22} />
                </span>
                <p className="font-display text-lg sm:text-xl font-bold tracking-wide">{p.missionLabel}</p>
              </div>
              <h3 className="mt-8 font-display font-semibold text-xl sm:text-2xl lg:text-3xl leading-relaxed" data-testid="profile-mission-text">
                {p.mission}
              </h3>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-7">
            <div data-testid="profile-message-card" className="relative h-full rounded-[2rem] p-8 sm:p-10 bg-gradient-to-br from-[#ABCF39] to-[#DAE13B] text-[#383838] flex flex-col justify-between overflow-hidden">
              <span className="absolute -top-12 -start-12 w-44 h-44 rounded-full bg-white/20" />
              <div className="relative flex items-center gap-4">
                <span className="w-12 h-12 rounded-2xl bg-[#383838] text-[#DAE13B] flex items-center justify-center shrink-0">
                  <Compass size={22} />
                </span>
                <p className="font-display text-lg sm:text-xl font-bold tracking-wide">{p.messageLabel}</p>
              </div>
              <div className="relative mt-8">
                <p className="font-display font-semibold text-xl sm:text-2xl lg:text-3xl leading-relaxed" data-testid="profile-message-text">
                  {p.message}
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="mt-20 sm:mt-24">
          <Reveal>
            <Eyebrow>{p.valuesEyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-display font-bold tracking-tight leading-snug text-2xl sm:text-3xl lg:text-4xl text-[#383838]" data-testid="profile-values-title">
              {p.valuesTitle}
            </h2>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {p.values.map((v, i) => {
              const Icon = ICONS[i];
              return (
                <Reveal key={v.title} delay={0.08 * i}>
                  <motion.div
                    whileHover={{ y: -6 }}
                    transition={{ type: "spring", stiffness: 300, damping: 22 }}
                    data-testid={`profile-value-card-${i}`}
                    className="group h-full rounded-3xl bg-[#FAFAFA] border border-slate-200/80 p-7 hover:border-[#ABCF39] hover:shadow-[0_24px_60px_-24px_rgba(171,207,57,0.5)] transition-[border-color,box-shadow]"
                  >
                    <div className="flex items-center justify-between">
                      <span className="w-12 h-12 rounded-2xl bg-[#ABCF39]/15 text-[#7fa315] flex items-center justify-center group-hover:bg-[#ABCF39] group-hover:text-[#383838] transition-colors">
                        <Icon size={22} />
                      </span>
                      <span className="font-display text-sm font-bold text-slate-300">0{i + 1}</span>
                    </div>
                    <h4 className="mt-6 font-display font-bold text-lg sm:text-xl text-[#383838]">{v.title}</h4>
                    <p className="mt-2 text-sm sm:text-base leading-relaxed text-slate-600">{v.desc}</p>
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
